import { useRef } from 'react';
import maplibregl from 'maplibre-gl';
import { SELECTED_LOT_STYLE } from '../styles/mapTheme';
import { canAccessMapStyle } from '../utils/canAccessMapStyle';
import { getLotById } from '../../services/lotsService';
import { parseLotViewQuotaFromResult, isLotViewQuotaForbiddenResult } from '../utils/lotViewQuota';

/**
 * Punto representativo del lote: coordenadas del backend o centro aproximado del perímetro.
 */
export function getLotRepresentativeLatLng(lotData) {
  if (!lotData || typeof lotData !== 'object') return null;

  const rawLat = lotData.lat ?? lotData.latitude;
  const rawLng = lotData.lng ?? lotData.longitude ?? lotData.lon;
  const lat = rawLat !== undefined && rawLat !== null && rawLat !== '' ? Number(rawLat) : NaN;
  const lng = rawLng !== undefined && rawLng !== null && rawLng !== '' ? Number(rawLng) : NaN;
  if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
    return { lat, lng };
  }

  const units = Array.isArray(lotData.units) ? lotData.units : [];
  for (const unit of units) {
    const uLat = unit?.lat ?? unit?.latitude;
    const uLng = unit?.lng ?? unit?.longitude ?? unit?.lon;
    const uLatN = uLat !== undefined && uLat !== null && uLat !== '' ? Number(uLat) : NaN;
    const uLngN = uLng !== undefined && uLng !== null && uLng !== '' ? Number(uLng) : NaN;
    if (!Number.isNaN(uLatN) && !Number.isNaN(uLngN)) {
      return { lat: uLatN, lng: uLngN };
    }
  }

  if (!Array.isArray(lotData.boundary) || lotData.boundary.length === 0) return null;

  let pts = lotData.boundary.map((c) => ({
    lat: Number(c.lat),
    lng: Number(c.lng),
  }));
  if (pts.some((p) => Number.isNaN(p.lat) || Number.isNaN(p.lng))) return null;

  if (pts.length > 1) {
    const a = pts[0];
    const b = pts[pts.length - 1];
    if (a.lat === b.lat && a.lng === b.lng) pts = pts.slice(0, -1);
  }
  if (pts.length === 0) return null;

  const sum = pts.reduce((acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }), { lat: 0, lng: 0 });
  return { lat: sum.lat / pts.length, lng: sum.lng / pts.length };
}

/**
 * Construye el estado del polígono/LOTE en la forma que espera el `DockPanel`/tabs.
 * Se exporta para que páginas públicas (ej. `SharedUserLotPage`) puedan reutilizar la misma estructura.
 * @see docs/maps/lot-detail-field-parity.md
 */
export function buildLotSelectionState({ polygon, lotData }) {
  const baseStreet =
    lotData.street_name || lotData.street || lotData.lot_street || '';

  const representative = getLotRepresentativeLatLng(lotData);

  const streetNumber =
    lotData.street_number !== null &&
    lotData.street_number !== undefined &&
    String(lotData.street_number).trim() !== ''
      ? String(lotData.street_number).trim()
      : '';

  const combinedStreet =
    baseStreet && streetNumber ? `${baseStreet} ${streetNumber}` : baseStreet;

  return {
    polygon,
    id: lotData.id,
    name: lotData.name,
    surface: lotData.surface,
    urbansurface: lotData.urbansurface,
    built_footprint_m2: lotData.built_footprint_m2 ?? lotData.lot_built_footprint_m2 ?? null,
    max_footprint_m2: lotData.max_footprint_m2 ?? lotData.lot_max_footprint_m2 ?? null,
    max_buildable_m2: lotData.max_buildable_m2 ?? lotData.lot_max_buildable_m2 ?? null,
    state: lotData.state,
    type: lotData.type,
    valuation: lotData.valuation,
    province: lotData.province || lotData.province_name,
    department: lotData.department || lotData.department_name,
    locality: lotData.locality_name || lotData.city || lotData.lot_city || '',
    municipality: lotData.municipality_name || lotData.municipality || '',
    neighborhood: lotData.neighborhood || lotData.neighborhood_name || '',
    street_name: baseStreet,
    street: combinedStreet,
    companies: lotData.companies || [],
    isLotPolygon: true,
    lat: representative?.lat,
    lng: representative?.lng,
    zoning_zone: lotData.zoning_zone ?? null,
    zoning_perfil: lotData.zoning_perfil ?? null,
    flood_risk_pct: lotData.flood_risk_pct ?? null,
    chronic_poverty_pct: lotData.chronic_poverty_pct ?? null,
    population_density: lotData.population_density ?? null,
    front_highway_type: lotData.front_highway_type ?? lotData.lot_front_highway_type ?? null,
    street_position: lotData.street_position ?? lotData.lot_street_position ?? null,
    neighborhood_type: lotData.neighborhood_type ?? lotData.lot_neighborhood_type ?? null,
    lot_shape: lotData.lot_shape ?? null,
    front_azimuth: lotData.front_azimuth ?? lotData.lot_front_azimuth ?? null,
    water_network_deficit_pct: lotData.water_network_deficit_pct ?? null,
    sewer_deficit_pct: lotData.sewer_deficit_pct ?? null,
    cooking_fuel_deficit_pct: lotData.cooking_fuel_deficit_pct ?? null,
    boundary: Array.isArray(lotData.boundary) ? lotData.boundary : null,
  };
}

/**
 * Maneja la selección, carga y dibujado de lotes en el mapa.
 *
 * @param {object} options
 * @param {React.MutableRefObject} options.mapInstanceRef  — ref a la instancia del mapa
 * @param {Function} options.setSelectedPolygon            — setter del estado en el componente padre
 */
export const getFeatureLotId = (feature) => {
  if (!feature || typeof feature !== 'object') return null;
  if (feature.id !== undefined && feature.id !== null && String(feature.id).trim() !== '') {
    return String(feature.id).trim();
  }
  const props = feature.properties || {};
  const candidates = [props.id, props.Id, props.lot_id, props.lotId, props.lote_id, props.uuid];
  const found = candidates.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
  return found != null ? String(found).trim() : null;
};

const getFeatureApproxArea = (feature) => {
  const geometry = feature?.geometry;
  if (!geometry || !Array.isArray(geometry.coordinates)) return Number.POSITIVE_INFINITY;

  let minLng = Number.POSITIVE_INFINITY;
  let minLat = Number.POSITIVE_INFINITY;
  let maxLng = Number.NEGATIVE_INFINITY;
  let maxLat = Number.NEGATIVE_INFINITY;
  let hasPoint = false;

  const consumePoint = (point) => {
    if (!Array.isArray(point) || point.length < 2) return;
    const lng = Number(point[0]);
    const lat = Number(point[1]);
    if (Number.isNaN(lng) || Number.isNaN(lat)) return;
    hasPoint = true;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  };

  if (geometry.type === 'Polygon') {
    geometry.coordinates.forEach((ring) => {
      if (!Array.isArray(ring)) return;
      ring.forEach(consumePoint);
    });
  } else if (geometry.type === 'MultiPolygon') {
    geometry.coordinates.forEach((polygon) => {
      if (!Array.isArray(polygon)) return;
      polygon.forEach((ring) => {
        if (!Array.isArray(ring)) return;
        ring.forEach(consumePoint);
      });
    });
  } else {
    return Number.POSITIVE_INFINITY;
  }

  if (!hasPoint) return Number.POSITIVE_INFINITY;
  return Math.abs((maxLng - minLng) * (maxLat - minLat));
};

export function pickBestLotFeatureAtPoint(map, point, layerIds) {
  if (!map || !point) return null;

  const interactiveLayers = (layerIds || []).filter((layerId) => map.getLayer(layerId));
  if (!interactiveLayers.length) return null;

  const hits = map.queryRenderedFeatures(point, { layers: interactiveLayers }) || [];
  if (!hits.length) return null;

  let bestFeature = null;
  let bestArea = Number.POSITIVE_INFINITY;
  hits.forEach((feature) => {
    if (!getFeatureLotId(feature)) return;
    const area = getFeatureApproxArea(feature);
    if (area < bestArea) {
      bestFeature = feature;
      bestArea = area;
    }
  });
  return bestFeature;
}

const isAbortError = (error) => error?.name === 'AbortError';

export function useLotSelection({ mapInstanceRef, setSelectedPolygon, onQuotaInfo } = {}) {
  const polygonsRef = useRef([]);
  const abortControllerRef = useRef(null);
  const onQuotaInfoRef = useRef(onQuotaInfo);
  onQuotaInfoRef.current = onQuotaInfo;

  const beginLotSelectionFetch = () => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return controller.signal;
  };

  const applyQuotaFromResult = (result) => {
    const quota = parseLotViewQuotaFromResult(result);
    if (quota) onQuotaInfoRef.current?.(quota);
    return quota;
  };

  // ─── Dibujado ──────────────────────────────────────────────────────────────

  const drawLotPolygon = (lotData, quota = null) => {
    if (!lotData.boundary || !Array.isArray(lotData.boundary) || lotData.boundary.length === 0) {
      console.error('Invalid boundary data for lot:', lotData);
      return null;
    }

    let coordinates = lotData.boundary.map((coord) => [coord.lng, coord.lat]);
    // Cerrar el polígono si es necesario
    if (
      coordinates.length > 0 &&
      (coordinates[0][0] !== coordinates[coordinates.length - 1][0] ||
        coordinates[0][1] !== coordinates[coordinates.length - 1][1])
    ) {
      coordinates.push([...coordinates[0]]);
    }

    const geojson = {
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [coordinates] },
      properties: {
        id: lotData.id,
        name: lotData.name,
        surface: lotData.surface,
        urbansurface: lotData.urbansurface,
        state: lotData.state,
        type: lotData.type,
        valuation: lotData.valuation,
        companies: lotData.companies || [],
        isLotPolygon: true,
      },
    };

    const sourceId = `lot-${lotData.id}`;
    const layerId = `lot-layer-${lotData.id}`;
    const map = mapInstanceRef.current;
    // Sin mapa (WebGL no disponible / desmontado): la ficha se abre igual, sin polígono.
    if (!canAccessMapStyle(map)) return { id: sourceId, layerId };

    // Limpiar si ya existe
    if (map.getSource(sourceId)) {
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getLayer(`${layerId}-border`)) map.removeLayer(`${layerId}-border`);
      map.removeSource(sourceId);
    }

    map.addSource(sourceId, { type: 'geojson', data: geojson });

    map.addLayer({
      id: layerId,
      type: 'fill',
      source: sourceId,
      paint: {
        'fill-color': SELECTED_LOT_STYLE.fillColor,
        'fill-opacity': SELECTED_LOT_STYLE.fillOpacity,
        'fill-outline-color': SELECTED_LOT_STYLE.outlineColor,
      },
    });

    map.addLayer({
      id: `${layerId}-border`,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': SELECTED_LOT_STYLE.borderColor,
        'line-width': SELECTED_LOT_STYLE.borderWidth,
        'line-opacity': SELECTED_LOT_STYLE.borderOpacity,
      },
    });

    const handleClick = (e) => {
      if (e.features.length > 0 && e.features[0].source === sourceId) {
        setSelectedPolygon(buildPolygonState({ polygon: { id: sourceId, layerId }, lotData, quota }));
      }
    };

    map.on('click', layerId, handleClick);

    polygonsRef.current.push({
      polygon: { id: sourceId, layerId, clickHandler: handleClick },
      isLotPolygon: true,
      lotData,
      quota,
    });

    // Encuadrar mapa al polígono, dejando más espacio del lado izquierdo
    // para que no quede tapado por el DockPanel.
    const bounds = new maplibregl.LngLatBounds();
    coordinates.forEach((coord) => bounds.extend(coord));

    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    map.fitBounds(bounds, {
      padding: isMobile
        ? 50
        : {
            top: 50,
            bottom: 50,
            left: 400, // ancho aproximado del panel lateral
            right: 80,
          },
    });

    //console.log('Lot polygon drawn:', lotData.name);
    return { id: sourceId, layerId };
  };

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const buildPolygonState = ({ polygon, lotData, quota }) => ({
    ...buildLotSelectionState({ polygon, lotData }),
    quota: quota || null,
  });

  const handleLotResponse = (lotData, quota) => {
    const polygon = drawLotPolygon(lotData, quota);
    setSelectedPolygon(buildPolygonState({ polygon, lotData, quota }));
  };

  // ─── Limpieza ──────────────────────────────────────────────────────────────

  const clearLotPolygons = () => {
    const map = mapInstanceRef.current;
    if (!map) return;

    polygonsRef.current.forEach(({ isLotPolygon, polygon }) => {
      if (!isLotPolygon) return;
      if (map.getSource(polygon.id)) {
        map.removeLayer(polygon.layerId);
        map.removeLayer(`${polygon.layerId}-border`);
        map.removeSource(polygon.id);
        if (polygon.clickHandler) map.off('click', polygon.layerId, polygon.clickHandler);
      }
    });
    polygonsRef.current = polygonsRef.current.filter((p) => !p.isLotPolygon);
  };

  // ─── Fetch ─────────────────────────────────────────────────────────────────

  const fetchLotById = async (lotId, { signal: externalSignal } = {}) => {
    const signal = externalSignal ?? beginLotSelectionFetch();

    try {
      const result = await getLotById(lotId, { signal });
      const quota = applyQuotaFromResult(result);

      if (!result._httpOk) {
        if (isLotViewQuotaForbiddenResult(result)) {
          return { lotViewQuotaBlocked: true, quota };
        }
        console.error('API Error fetching lot by ID:', result._httpStatus);
        return null;
      }
      if (!result.data) return null;

      handleLotResponse(result.data, quota);
      return result.data;
    } catch (error) {
      if (isAbortError(error)) return null;
      console.error('Error fetching lot by ID:', error);
      return null;
    }
  };

  const fetchLotByIdReturningData = async (lotId) => {
    try {
      const result = await getLotById(lotId);
      applyQuotaFromResult(result);
      if (!result._httpOk) return null;
      return result.data || null;
    } catch (error) {
      console.error('Error fetching lot by ID:', error);
      return null;
    }
  };

  return {
    polygonsRef,
    drawLotPolygon,
    clearLotPolygons,
    fetchLotById,
    fetchLotByIdReturningData,
  };
}